import {
  Home,
  S3_BUCKET_URL,
  StoreThemeEnum,
  defaultDesignValues,
  useDesignContext,
} from '@systemeio/digital-store-shared'
import { StoreFontPicker } from 'modules/stores/store/components/design/store-font-picker'
import ItemSteps from 'modules/stores/store/components/item-steps'
import { STORE_ITEM_CLASS_NAME } from 'modules/stores/store/constants/store-item-class-name'
import { useStorePreviewContext } from 'modules/stores/store/context/store-preview-context'
import { StoreInterface } from 'modules/stores/types/stores-interface'
import React, { FC, useEffect } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twJoin, twMerge } from 'tailwind-merge'
import PreviewBoxMobile from '../preview-box-mobile'
import { StoreThemeColorPicker } from './store-theme-color-picker'
import { StoreThemeSaveButton } from './store-theme-save-button'
import { CheckCircleIcon, SmallPenIcon } from '@systemeio/ui/icons'

interface StoreEditDesignProps {
  store: StoreInterface
}

const StoreEditDesign: FC<StoreEditDesignProps> = ({ store }) => {
  const { t } = useLocoTranslation()

  const { setPreview, setSingleItemMode } = useStorePreviewContext()

  const { state, setState } = useDesignContext()

  useEffect(() => {
    setState(store.design)
    return () => setState(store.design)
  }, [setState, store.design])

  useEffect(() => {
    setPreview([
      <Home
        key={store.id}
        profile={store.profile}
        socialLinks={store.socialLinks}
        items={store.items.filter(el => !el.draft)}
        displayAffiliateLink={false}
      />,
    ])
    setSingleItemMode(false)
  }, [setPreview, setSingleItemMode, store])

  return (
    <div className={twMerge(STORE_ITEM_CLASS_NAME, 'flex flex-col gap-5')}>
      <ItemSteps
        steps={[
          {
            key: 'theme',
            title: t('dashboard.store.theme.select_theme'),
            element: (
              <div className={'grid w-full grid-cols-2 justify-items-center gap-5 xl:grid-cols-3'}>
                {Object.values(StoreThemeEnum).map(theme => {
                  const isSelected = theme === state.theme
                  const defaultValue = defaultDesignValues[theme]
                  return (
                    <button
                      onClick={() => !isSelected && setState(defaultValue)}
                      key={theme}
                      className={twJoin(
                        'shadow-around-sm main-transition-colors relative aspect-[1/2] w-full max-w-[350px] overflow-y-hidden rounded-lg border outline-hidden',
                        isSelected ? 'border-brand-primary' : 'border-text-secondary/30',
                      )}
                    >
                      {isSelected && (
                        <CheckCircleIcon className={'fill-brand-primary absolute left-1 top-1'} />
                      )}
                      <img
                        alt={theme}
                        src={`${S3_BUCKET_URL}/images/theme-preview/${theme}-preview.png`}
                      />
                    </button>
                  )
                })}
              </div>
            ),
          },
        ]}
      />

      <div
        className={
          'bg-background-secondary sticky bottom-0 -mb-5 -ml-5 box-content flex w-full flex-wrap items-end justify-between gap-x-5 gap-y-3 rounded-b-md p-5 sm:justify-end lg:justify-between'
        }
      >
        <div className={'hidden lg:block'}>
          <StoreThemeColorPicker />
        </div>

        <div className={'hidden lg:block'}>
          <StoreFontPicker />
        </div>

        <PreviewBoxMobile
          title={t('dashboard.store.theme.edit_theme')}
          icon={<SmallPenIcon />}
          className="flex-1 sm:flex-initial"
        />

        <StoreThemeSaveButton
          title={t('dashboard.store.theme.apply_theme')}
          className={'w-fit flex-1 sm:flex-initial'}
        />
      </div>
    </div>
  )
}

export default StoreEditDesign
